/**
 * Claims Review — Knowledge Claims Review Queue
 * Route: /claims
 *
 * Keyboard-first daily driver for clearing the claims backlog.
 * j/k navigate, single-key verdicts, batch-with-visibility.
 * Follows DiscoveryReview interaction model; adapted for claim verdicts.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Badge, Button, Spinner } from 'flowbite-react';
import {
  HiCheck,
  HiX,
  HiRefresh,
  HiExclamation,
  HiClock,
  HiChevronDown,
  HiChevronUp,
  HiExclamationCircle,
  HiFilter,
  HiSearch,
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { claimsApi } from '../api/claims';
import type { Claim, ClaimsListParams, ClaimVerdict, TranscriptChunkResponse, ConversationGroup, SubjectGroup } from '../api/claims';

const REASON_CODES = [
  { value: 'duplicate', label: 'Duplicate', key: '1' },
  { value: 'contradicts_canonical', label: 'Contradicts Canonical', key: '2' },
  { value: 'quality_issues', label: 'Quality', key: '3' },
  { value: 'insufficient_evidence', label: 'No Evidence', key: '4' },
] as const;

const DEFER_PRESETS = [
  { label: '1 day', days: 1, key: '1' },
  { label: '3 days', days: 3, key: '2' },
  { label: '1 week', days: 7, key: '3' },
  { label: '2 weeks', days: 14, key: '4' },
] as const;

const WEIGHT_LABELS: Record<string, string> = {
  '0.40': 'ruling',
  '0.32': 'adopted',
  '0.28': 'fact',
  '0.22': 'unwitnessed',
  '0.20': 'default',
};

type InlineMode = null | 'reject' | 'defer';

const ClaimsReview: React.FC = () => {
  const { user, hasFeature } = useStatus();
  const isCurator = hasFeature('audit_trail');

  const [claims, setClaims] = useState<Claim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // Keyboard navigation
  const [focusIndex, setFocusIndex] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inlineMode, setInlineMode] = useState<InlineMode>(null);
  const [retractMode, setRetractMode] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [conflictReviewed, setConflictReviewed] = useState<Set<string>>(new Set());
  const [excerpts, setExcerpts] = useState<Record<string, TranscriptChunkResponse | 'loading' | 'error'>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Grouping
  const [viewMode, setViewMode] = useState<'flat' | 'conversation' | 'subject'>('flat');
  const [groups, setGroups] = useState<ConversationGroup[]>([]);
  const [subjectGroups, setSubjectGroups] = useState<SubjectGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [totalGroups, setTotalGroups] = useState(0);
  const [groupSort, setGroupSort] = useState<'size' | 'date'>('size');

  // Filters
  const [statusFilter, setStatusFilter] = useState('proposed');
  const [domainFilter, setDomainFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [provenanceFilter, setProvenanceFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Aggregates
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [domains, setDomains] = useState<Array<{ domain: string; cnt: number }>>([]);
  const [projects, setProjects] = useState<Array<{ source_project: string; cnt: number }>>([]);

  useEffect(() => { loadCounts(); }, []);
  useEffect(() => { loadClaims(); }, [statusFilter, domainFilter, projectFilter, provenanceFilter, scopeFilter, page, viewMode, groupSort]);

  const loadCounts = async () => {
    try {
      const response = await claimsApi.getCounts();
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setCounts(data.status_counts || {});
        setDomains(data.domain_counts || []);
        setProjects(data.source_project_counts || []);
      }
    } catch { /* counts are supplementary */ }
  };

  const loadClaims = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const baseParams: ClaimsListParams = {
        status: statusFilter || 'proposed',
        page,
      };
      if (domainFilter) baseParams.domain = domainFilter;
      if (projectFilter) baseParams.source_project = projectFilter;
      if (provenanceFilter) baseParams.provenance_class = provenanceFilter;
      if (scopeFilter) baseParams.scope = scopeFilter;
      if (searchQuery) baseParams.q = searchQuery;

      if (viewMode === 'conversation') {
        baseParams.group_sort = groupSort;
        baseParams.group_by = 'conversation';
        const response = await claimsApi.listGrouped(baseParams);
        if (response.success && response.data) {
          const data = (response.data as any).data || response.data;
          const grps = (data.groups || []) as ConversationGroup[];
          setGroups(grps);
          setSubjectGroups([]);
          setTotalGroups(data.pagination?.total_groups || 0);
          setTotalCount(grps.reduce((s: number, g: ConversationGroup) => s + g.claims.length, 0));
          const allClaims = grps.flatMap((g: ConversationGroup) => g.claims);
          setClaims(allClaims);
          setFocusIndex(0);
          setSelected(new Set());
          setInlineMode(null);
          if (grps.length > 0) {
            setExpandedGroups(new Set([grps[0].conversation_id]));
          }
        } else {
          setError(response.error || 'Failed to load claims');
        }
      } else if (viewMode === 'subject') {
        baseParams.group_by = 'subject';
        const response = await claimsApi.listGrouped(baseParams);
        if (response.success && response.data) {
          const data = (response.data as any).data || response.data;
          const grps = (data.groups || []) as SubjectGroup[];
          setSubjectGroups(grps);
          setGroups([]);
          setTotalGroups(data.pagination?.total_groups || 0);
          setTotalCount(data.total_claims || grps.reduce((s: number, g: SubjectGroup) => s + g.claims.length, 0));
          const allClaims = grps.flatMap((g: SubjectGroup) => g.claims);
          setClaims(allClaims);
          setFocusIndex(0);
          setSelected(new Set());
          setInlineMode(null);
          if (grps.length > 0) {
            setExpandedGroups(new Set([grps[0].subject]));
          }
        } else {
          setError(response.error || 'Failed to load claims');
        }
      } else {
        baseParams.limit = 50;
        const response = await claimsApi.list(baseParams);
        if (response.success && response.data) {
          const data = (response.data as any).data || response.data;
          setClaims(data.records || []);
          setTotalCount(data.pagination?.total || 0);
          setFocusIndex(0);
          setSelected(new Set());
          setInlineMode(null);
        } else {
          setError(response.error || 'Failed to load claims');
        }
      }
    } catch {
      setError('Failed to load claims');
    } finally {
      setIsLoading(false);
    }
  };

  const doSearch = () => {
    setPage(1);
    loadClaims();
  };

  const toggleGroupSelection = useCallback((group: ConversationGroup | SubjectGroup) => {
    const groupClaimIds = group.claims.map(c => c.claim_id);
    const allSelected = groupClaimIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        groupClaimIds.forEach(id => next.delete(id));
      } else {
        groupClaimIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [selected]);

  const fetchExcerpt = useCallback(async (conversationId: string, claimId: string) => {
    const key = claimId;
    if (excerpts[key]) return;
    setExcerpts(prev => ({ ...prev, [key]: 'loading' }));
    try {
      const response = await claimsApi.getTranscriptContext(conversationId, claimId);
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setExcerpts(prev => ({ ...prev, [key]: data as TranscriptChunkResponse }));
      } else {
        setExcerpts(prev => ({ ...prev, [key]: 'error' }));
      }
    } catch {
      setExcerpts(prev => ({ ...prev, [key]: 'error' }));
    }
  }, [excerpts]);

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const removeCard = useCallback((id: string) => {
    setClaims(prev => prev.filter(c => c.claim_id !== id));
    setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
  }, []);

  const doGroupAction = useCallback(async (group: ConversationGroup | SubjectGroup, verdict: ClaimVerdict) => {
    const targets = group.claims;
    const hasConflict = targets.some(c => c.canonical_conflicts && c.canonical_conflicts.length > 0);
    if (verdict === 'confirm' && hasConflict) {
      showToast('Group contains canonical conflicts — confirm conflicted claims individually', 'error');
      return;
    }
    setIsActioning('batch');
    try {
      const response = await claimsApi.review({
        claim_ids: targets.map(c => c.claim_id),
        verdict,
      });
      if (response.success) {
        const data = (response.data as any).data || response.data;
        const succeeded = data?.succeeded || 0;
        targets.forEach(t => removeCard(t.claim_id));
        if ('conversation_id' in group) {
          setGroups(prev => prev.filter(g => g.conversation_id !== group.conversation_id));
        } else {
          setSubjectGroups(prev => prev.filter(g => g.subject !== (group as SubjectGroup).subject));
        }
        const verb = verdict === 'confirm' ? 'Confirmed' : 'Deferred';
        showToast(`${verb} ${succeeded}/${targets.length} claims from group`);
        loadCounts();
      } else {
        showToast(response.error || 'Group action failed', 'error');
      }
    } catch {
      showToast('Group action failed', 'error');
    } finally {
      setIsActioning(null);
    }
  }, [removeCard, showToast]);

  const doAction = useCallback(async (
    claim: Claim,
    verdict: ClaimVerdict,
    extra?: { reason_code?: string; notes?: string; currency?: 'current' | 'historical_only' | 'superseded' }
  ) => {
    setIsActioning(claim.claim_id);
    try {
      const response = await claimsApi.review({
        claim_ids: [claim.claim_id],
        verdict,
        ...extra,
      });
      if (response.success) {
        removeCard(claim.claim_id);
        const verb = verdict === 'confirm' ? 'Confirmed' : verdict === 'dispute' ? 'Disputed' : verdict === 'retract' ? 'Retracted' : 'Deferred';
        showToast(`${verb}: ${claim.claim_text.slice(0, 60)}...`);
        loadCounts();
      } else {
        showToast(response.error || `Failed to ${verdict}`, 'error');
      }
    } catch {
      showToast(`Failed to ${verdict}`, 'error');
    } finally {
      setIsActioning(null);
      setInlineMode(null);
    }
  }, [removeCard, showToast]);

  const doBatchAction = useCallback(async (verdict: ClaimVerdict) => {
    if (selected.size === 0) return;
    const targets = claims.filter(c => selected.has(c.claim_id));
    setIsActioning('batch');
    try {
      const response = await claimsApi.review({
        claim_ids: targets.map(c => c.claim_id),
        verdict,
      });
      if (response.success) {
        const data = (response.data as any).data || response.data;
        const succeeded = data?.succeeded || 0;
        targets.forEach(t => removeCard(t.claim_id));
        const verb = verdict === 'confirm' ? 'Confirmed' : 'Deferred';
        showToast(`${verb} ${succeeded}/${targets.length} claims`);
        loadCounts();
      } else {
        showToast(response.error || 'Batch action failed', 'error');
      }
    } catch {
      showToast('Batch action failed', 'error');
    } finally {
      setIsActioning(null);
    }
  }, [selected, claims, removeCard, showToast]);

  const scrollToCard = useCallback((index: number) => {
    const el = cardRefs.current.get(index);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (inlineMode === 'reject') {
        const code = REASON_CODES.find(r => r.key === e.key);
        if (code && claims[focusIndex]) {
          e.preventDefault();
          doAction(claims[focusIndex], retractMode ? 'retract' : 'dispute', { reason_code: code.value });
          setRetractMode(false);
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); setRetractMode(false); return; }
        return;
      }

      if (inlineMode === 'defer') {
        const preset = DEFER_PRESETS.find(p => p.key === e.key);
        if (preset && claims[focusIndex]) {
          e.preventDefault();
          const deferDate = new Date();
          deferDate.setDate(deferDate.getDate() + preset.days);
          doAction(claims[focusIndex], 'defer', {
            notes: `deferred until ${deferDate.toISOString().slice(0, 10)}`,
          });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      switch (e.key) {
        case 'j': {
          e.preventDefault();
          const next = Math.min(focusIndex + 1, claims.length - 1);
          setFocusIndex(next);
          scrollToCard(next);
          break;
        }
        case 'k': {
          e.preventDefault();
          const prev = Math.max(focusIndex - 1, 0);
          setFocusIndex(prev);
          scrollToCard(prev);
          break;
        }
        case ' ': {
          e.preventDefault();
          const item = claims[focusIndex];
          if (!item) break;
          setSelected(prev => {
            const next = new Set(prev);
            if (next.has(item.claim_id)) next.delete(item.claim_id);
            else next.add(item.claim_id);
            return next;
          });
          break;
        }
        case 'a': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            const hasConflict = claims.some(c => selected.has(c.claim_id) && c.canonical_conflicts && c.canonical_conflicts.length > 0);
            if (hasConflict) {
              showToast('Batch contains canonical conflicts — confirm conflicted claims individually', 'error');
            } else {
              doBatchAction('confirm');
            }
          } else if (claims[focusIndex]) {
            const c = claims[focusIndex];
            const hasConflict = c.canonical_conflicts && c.canonical_conflicts.length > 0;
            if (hasConflict && !conflictReviewed.has(c.claim_id)) {
              showToast('Canonical conflict — expand (x) to review, then confirm', 'error');
            } else if (hasConflict) {
              doAction(c, 'confirm');
            } else {
              doAction(c, 'confirm');
            }
          }
          break;
        }
        case 'r': {
          e.preventDefault();
          if (claims[focusIndex]) setInlineMode('reject');
          break;
        }
        case 'd': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('defer');
          } else if (claims[focusIndex]) {
            setInlineMode('defer');
          }
          break;
        }
        case 't': {
          e.preventDefault();
          if (isCurator && claims[focusIndex]) {
            setInlineMode('reject');
            setRetractMode(true);
          }
          break;
        }
        case 'x': {
          e.preventDefault();
          const item = claims[focusIndex];
          if (!item) break;
          setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(item.claim_id)) next.delete(item.claim_id);
            else next.add(item.claim_id);
            return next;
          });
          if (item.canonical_conflicts && item.canonical_conflicts.length > 0) {
            setConflictReviewed(prev => new Set(prev).add(item.claim_id));
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          if (selected.size > 0) setSelected(new Set());
          break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusIndex, claims, inlineMode, selected, doAction, doBatchAction, scrollToCard]);

  useEffect(() => {
    if (focusIndex >= claims.length && claims.length > 0) {
      setFocusIndex(claims.length - 1);
    }
  }, [claims.length, focusIndex]);

  const getWeightBadge = (weight: number) => {
    const key = weight.toFixed(2);
    const label = WEIGHT_LABELS[key] || key;
    const color = weight >= 0.4 ? 'purple' : weight >= 0.28 ? 'blue' : 'gray';
    return <Badge color={color} className="text-xs">{label} ({key})</Badge>;
  };

  const getProvenanceBadge = (cls: string) => {
    const colors: Record<string, string> = {
      session_extraction: 'info',
      verified_execution: 'success',
      canonical_explainer: 'purple',
      llm_inference: 'warning',
      market_signal: 'pink',
    };
    return <Badge color={colors[cls] || 'gray'} className="text-xs">{cls}</Badge>;
  };

  if (isLoading && claims.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Claims Review</h1>
          <Badge color="info">{totalCount} claims</Badge>
          {viewMode !== 'flat' && <Badge color="purple">{totalGroups} groups</Badge>}
          {selected.size > 0 && (
            <Badge color="purple">{selected.size} selected</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            {(['flat', 'conversation', 'subject'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => { setViewMode(mode); setPage(1); }}
                className={`px-2 py-1 text-xs font-medium ${mode !== 'flat' ? 'border-l border-gray-300' : ''} ${
                  viewMode === mode ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >{mode === 'flat' ? 'Flat' : mode === 'conversation' ? 'By Conversation' : 'By Subject'}</button>
            ))}
          </div>
          {viewMode === 'conversation' && (
            <select
              value={groupSort}
              onChange={e => { setGroupSort(e.target.value as 'size' | 'date'); setPage(1); }}
              className="text-xs border-gray-300 rounded-md py-1"
            >
              <option value="size">Largest first</option>
              <option value="date">Newest first</option>
            </select>
          )}
          <Button size="xs" color="light" onClick={() => setShowFilters(!showFilters)}>
            <HiFilter className="h-4 w-4 mr-1" />
            Filters
          </Button>
          <Button size="xs" color="light" onClick={() => { setPage(1); loadClaims(); loadCounts(); }} disabled={isLoading}>
            <HiRefresh className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-2 text-sm flex-wrap">
        {['proposed', 'active', 'disputed', 'retracted', 'all'].map(s => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusFilter === s ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {s} {counts[s] !== undefined ? `(${counts[s]})` : ''}
          </button>
        ))}
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Domain</label>
              <select value={domainFilter} onChange={e => { setDomainFilter(e.target.value); setPage(1); }}
                className="w-full text-sm border-gray-300 rounded-md">
                <option value="">All domains</option>
                {domains.map(d => (
                  <option key={d.domain} value={d.domain === '(none)' ? '' : d.domain}>{d.domain} ({d.cnt})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Source Project</label>
              <select value={projectFilter} onChange={e => { setProjectFilter(e.target.value); setPage(1); }}
                className="w-full text-sm border-gray-300 rounded-md">
                <option value="">All projects</option>
                {projects.map(p => (
                  <option key={p.source_project} value={p.source_project === '(none)' ? '' : p.source_project}>{p.source_project} ({p.cnt})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Provenance</label>
              <select value={provenanceFilter} onChange={e => { setProvenanceFilter(e.target.value); setPage(1); }}
                className="w-full text-sm border-gray-300 rounded-md">
                <option value="">All</option>
                <option value="session_extraction">session_extraction</option>
                <option value="verified_execution">verified_execution</option>
                <option value="llm_inference">llm_inference</option>
                <option value="canonical_explainer">canonical_explainer</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Scope</label>
              <select value={scopeFilter} onChange={e => { setScopeFilter(e.target.value); setPage(1); }}
                className="w-full text-sm border-gray-300 rounded-md">
                <option value="">All scopes</option>
                <option value="project-specific">project-specific</option>
                <option value="organization-wide">organization-wide</option>
                <option value="unknown">unknown</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
                  placeholder="Search claim text..."
                  className="flex-1 text-sm border-gray-300 rounded-md"
                />
                <Button size="xs" color="light" onClick={doSearch}>
                  <HiSearch className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${
          toast.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {toast.type === 'success' ? <HiCheck className="h-4 w-4" /> : <HiExclamation className="h-4 w-4" />}
          {toast.text}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <HiExclamation className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Keyboard legend */}
      <div className="text-xs text-gray-400 flex items-center gap-3 flex-wrap">
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">j</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">k</kbd> navigate</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">a</kbd> confirm</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">r</kbd> dispute</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">d</kbd> defer</span>
        {isCurator && <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">t</kbd> retract</span>}
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">x</kbd> expand</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">space</kbd> select</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">Shift+a</kbd> batch confirm</span>
        <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">Shift+d</kbd> batch defer</span>
      </div>

      {/* Batch action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <span className="text-sm font-medium text-purple-700">
            {selected.size} selected:
          </span>
          <span className="text-xs text-purple-600 max-w-lg truncate">
            {claims.filter(c => selected.has(c.claim_id)).map(c => c.claim_text.slice(0, 40)).join(' | ')}
          </span>
          <div className="ml-auto flex gap-2">
            <Button size="xs" color="success" onClick={() => {
              const hasConflict = claims.some(c => selected.has(c.claim_id) && c.canonical_conflicts && c.canonical_conflicts.length > 0);
              if (hasConflict) {
                showToast('Batch contains canonical conflicts — confirm conflicted claims individually', 'error');
              } else {
                doBatchAction('confirm');
              }
            }} disabled={isActioning === 'batch'}>
              Confirm all
            </Button>
            <Button size="xs" color="warning" onClick={() => doBatchAction('defer')} disabled={isActioning === 'batch'}>
              Defer all
            </Button>
            <Button size="xs" color="light" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size="xl" /></div>
      ) : claims.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">
            {statusFilter === 'proposed' ? 'No claims awaiting review.' : 'No claims match the current filters.'}
          </p>
        </div>
      ) : (viewMode === 'conversation' || viewMode === 'subject') ? (
        <div className="space-y-3">
          {(viewMode === 'conversation' ? groups : subjectGroups).map(group => {
            const groupKey = 'conversation_id' in group ? group.conversation_id : (group as SubjectGroup).subject;
            const isGroupExpanded = expandedGroups.has(groupKey);
            const groupClaimIds = group.claims.map(c => c.claim_id);
            const allGroupSelected = groupClaimIds.length > 0 && groupClaimIds.every(id => selected.has(id));
            const someGroupSelected = groupClaimIds.some(id => selected.has(id));
            const hasConflict = group.claims.some(c => c.canonical_conflicts && c.canonical_conflicts.length > 0);
            const conflictCount = group.claims.filter(c => c.canonical_conflicts && c.canonical_conflicts.length > 0).length;
            const isConvGroup = 'conversation_id' in group;

            return (
              <div key={groupKey} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Group header */}
                <div
                  className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer ${
                    isGroupExpanded ? 'bg-blue-50 border-b border-blue-100' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => setExpandedGroups(prev => {
                    const next = new Set(prev);
                    if (next.has(groupKey)) next.delete(groupKey);
                    else next.add(groupKey);
                    return next;
                  })}
                >
                  <input
                    type="checkbox"
                    checked={allGroupSelected}
                    ref={el => { if (el) el.indeterminate = someGroupSelected && !allGroupSelected; }}
                    onChange={e => { e.stopPropagation(); toggleGroupSelection(group); }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    tabIndex={-1}
                  />
                  {isGroupExpanded
                    ? <HiChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    : <HiChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isConvGroup ? (
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {group.claims[0]?.claim_text.slice(0, 80)}...
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">
                          <Badge color="purple" className="text-sm font-mono">{(group as SubjectGroup).subject}</Badge>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                      {isConvGroup && (
                        <>
                          <span className="font-mono">{(group as ConversationGroup).conversation_id.slice(0, 8)}</span>
                          <span>{new Date((group as ConversationGroup).earliest).toLocaleDateString()}</span>
                          {(group as ConversationGroup).projects.length > 0 && <span>{(group as ConversationGroup).projects.join(', ')}</span>}
                        </>
                      )}
                      {!isConvGroup && (
                        <span>{group.claims[0]?.claim_text.slice(0, 60)}...</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge color="info" className="text-xs">{group.claim_count} claims</Badge>
                    {hasConflict && <Badge color="failure" className="text-xs">{conflictCount} conflicts</Badge>}
                    {isGroupExpanded && (
                      <>
                        <Button size="xs" color="success" onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          doGroupAction(group, 'confirm');
                        }} disabled={isActioning === 'batch'}>
                          Confirm {group.claim_count}
                        </Button>
                        <Button size="xs" color="warning" onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          doGroupAction(group, 'defer');
                        }} disabled={isActioning === 'batch'}>
                          Defer {group.claim_count}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Group claims */}
                {isGroupExpanded && (
                  <div className="divide-y divide-gray-100">
                    {group.claims.map(claim => {
                      const idx = claims.findIndex(c => c.claim_id === claim.claim_id);
                      const isFocused = idx === focusIndex;
                      const isSelected = selected.has(claim.claim_id);
                      const isClaimExpanded = expanded.has(claim.claim_id);
                      const isProcessing = isActioning === claim.claim_id || isActioning === 'batch';

                      return (
                        <div
                          key={claim.claim_id}
                          ref={el => { if (el && idx >= 0) cardRefs.current.set(idx, el); }}
                          onClick={() => { if (idx >= 0) setFocusIndex(idx); }}
                          className={`px-4 py-2 transition-all cursor-pointer ${
                            isFocused ? 'bg-blue-50/50 ring-1 ring-inset ring-blue-300' :
                            isSelected ? 'bg-purple-50/30' : 'bg-white hover:bg-gray-50/50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setSelected(prev => {
                                  const next = new Set(prev);
                                  if (next.has(claim.claim_id)) next.delete(claim.claim_id);
                                  else next.add(claim.claim_id);
                                  return next;
                                });
                              }}
                              className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              tabIndex={-1}
                            />
                            <div className="flex-1 min-w-0">
                              {claim.canonical_conflicts && claim.canonical_conflicts.length > 0 && (
                                <div className="mb-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-center gap-1">
                                  <HiExclamationCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                  Canonical conflict
                                </div>
                              )}
                              <p className={`text-sm text-gray-900 ${isClaimExpanded ? '' : 'line-clamp-2'}`}>
                                {claim.claim_text}
                              </p>
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                {getWeightBadge(claim.provenance_weight)}
                                {claim.chunk_index !== null && <Badge color="gray" className="text-xs">chunk {claim.chunk_index}</Badge>}
                                {claim.domain && <Badge color="gray" className="text-xs">{claim.domain}</Badge>}
                                {claim.currency === 'historical_only' && <Badge color="dark" className="text-xs">historical</Badge>}
                                {claim.currency === 'superseded' && <Badge color="failure" className="text-xs">superseded</Badge>}
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  const hasC = claim.canonical_conflicts && claim.canonical_conflicts.length > 0;
                                  if (hasC && !conflictReviewed.has(claim.claim_id)) {
                                    showToast('Canonical conflict — expand to review first', 'error');
                                  } else {
                                    doAction(claim, 'confirm');
                                  }
                                }}
                                disabled={isProcessing}
                                className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                                tabIndex={-1}
                              >
                                <HiCheck className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={e => { e.stopPropagation(); if (idx >= 0) setFocusIndex(idx); setInlineMode('reject'); }}
                                disabled={isProcessing}
                                className="p-1 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                                tabIndex={-1}
                              >
                                <HiX className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setExpanded(prev => {
                                    const next = new Set(prev);
                                    if (next.has(claim.claim_id)) next.delete(claim.claim_id);
                                    else next.add(claim.claim_id);
                                    return next;
                                  });
                                  if (claim.canonical_conflicts && claim.canonical_conflicts.length > 0) {
                                    setConflictReviewed(prev => new Set(prev).add(claim.claim_id));
                                  }
                                }}
                                className="p-1 rounded text-gray-400 hover:text-gray-600"
                                tabIndex={-1}
                              >
                                {isClaimExpanded ? <HiChevronUp className="h-3.5 w-3.5" /> : <HiChevronDown className="h-3.5 w-3.5" />}
                              </button>
                            </div>
                          </div>
                          {isProcessing && isActioning === claim.claim_id && (
                            <div className="mt-1 ml-8 flex items-center gap-2 text-xs text-gray-500">
                              <Spinner size="xs" /> Processing...
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {claims.map((claim, idx) => {
            const isFocused = idx === focusIndex;
            const isSelected = selected.has(claim.claim_id);
            const isExpanded = expanded.has(claim.claim_id);
            const isProcessing = isActioning === claim.claim_id || isActioning === 'batch';
            const showRejectPicker = isFocused && inlineMode === 'reject';
            const showDeferPicker = isFocused && inlineMode === 'defer';

            return (
              <div
                key={claim.claim_id}
                ref={el => { if (el) cardRefs.current.set(idx, el); }}
                onClick={() => setFocusIndex(idx)}
                className={`rounded-lg border p-3 transition-all cursor-pointer ${
                  isFocused
                    ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/30'
                    : isSelected
                      ? 'border-purple-300 bg-purple-50/30'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {/* Main row */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      setSelected(prev => {
                        const next = new Set(prev);
                        if (next.has(claim.claim_id)) next.delete(claim.claim_id);
                        else next.add(claim.claim_id);
                        return next;
                      });
                    }}
                    className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    tabIndex={-1}
                  />

                  <div className="flex-1 min-w-0">
                    {/* Canonical conflict warning — must be visible before any confirm action */}
                    {claim.canonical_conflicts && claim.canonical_conflicts.length > 0 && (
                      <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 space-y-1">
                        <div className="flex items-center gap-1.5 font-medium">
                          <HiExclamationCircle className="h-4 w-4 flex-shrink-0" />
                          Canonical conflict — review before confirming
                        </div>
                        {claim.canonical_conflicts.map((c, i) => (
                          <div key={i} className="ml-5.5 pl-1">
                            {typeof c === 'object' && c.conflict_id
                              ? <span><span className="font-mono text-red-600">{c.conflict_id}</span>: {c.canonical_value}</span>
                              : <span>{String(c)}</span>
                            }
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Claim text */}
                    <p className={`text-sm text-gray-900 ${isExpanded ? '' : 'line-clamp-2'}`}>
                      {claim.claim_text}
                    </p>

                    {/* Badges row */}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {getWeightBadge(claim.provenance_weight)}
                      {getProvenanceBadge(claim.provenance_class)}
                      {claim.domain && <Badge color="gray" className="text-xs">{claim.domain}</Badge>}
                      {claim.scope && <Badge color="indigo" className="text-xs">scope: {claim.scope}</Badge>}
                      {claim.source_project && <Badge color="pink" className="text-xs">{claim.source_project}</Badge>}
                      {claim.claim_type && <Badge color="yellow" className="text-xs">{claim.claim_type}</Badge>}
                      {claim.currency === 'historical_only' && <Badge color="dark" className="text-xs">historical</Badge>}
                      {claim.currency === 'superseded' && <Badge color="failure" className="text-xs">superseded</Badge>}
                    </div>

                    {/* Authorities + flags */}
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                      {claim.asserting_authority && <span>asserted: {claim.asserting_authority}</span>}
                      {claim.adopting_authority && <span>adopted: {claim.adopting_authority}</span>}
                      {claim.valid_from && <span>from: {new Date(claim.valid_from).toLocaleDateString()}</span>}
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="mt-2 bg-gray-50 rounded p-3 border border-gray-100 space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div><span className="text-gray-400">ID:</span> <span className="font-mono text-gray-600">{claim.claim_id.slice(0, 8)}</span></div>
                          <div><span className="text-gray-400">Status:</span> <span className="text-gray-600">{claim.status}</span></div>
                          <div><span className="text-gray-400">Method:</span> <span className="text-gray-600">{claim.extraction_method}</span></div>
                          <div><span className="text-gray-400">Extracted by:</span> <span className="text-gray-600">{claim.extracted_by || '-'}</span></div>
                          <div><span className="text-gray-400">Valid from:</span> <span className="text-gray-600">{claim.valid_from ? new Date(claim.valid_from).toLocaleDateString() : '-'}</span></div>
                          <div><span className="text-gray-400">Confidence:</span> <span className="text-gray-600">{claim.computed_confidence?.toFixed(2) || 'null'}</span></div>
                          <div><span className="text-gray-400">Source type:</span> <span className="text-gray-600">{claim.source_object_type || '-'}</span></div>
                          <div><span className="text-gray-400">Source field:</span> <span className="font-mono text-gray-600 truncate">{claim.source_field ? claim.source_field.slice(0, 16) + '...' : '-'}</span></div>
                          {claim.temporal_class && <div><span className="text-gray-400">Temporal:</span> <span className="text-gray-600">{claim.temporal_class}</span></div>}
                          {claim.currency && <div><span className="text-gray-400">Currency:</span> <span className="text-gray-600">{claim.currency}</span></div>}
                          {claim.tags && claim.tags.length > 0 && (
                            <div className="col-span-2"><span className="text-gray-400">Tags:</span> <span className="text-gray-600">{claim.tags.join(', ')}</span></div>
                          )}
                        </div>
                        {/* Source context */}
                        {claim.conversation_id ? (
                          <div className="mt-2">
                            <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center justify-between">
                              <span>
                                <span className="text-gray-400">Conversation:</span>{' '}
                                <span className="font-mono">{claim.conversation_id.slice(0, 8)}</span>
                                {claim.chunk_index !== null && (
                                  <span className="ml-2"><span className="text-gray-400">Chunk:</span> {claim.chunk_index}</span>
                                )}
                              </span>
                              <button
                                onClick={e => { e.stopPropagation(); fetchExcerpt(claim.conversation_id!, claim.claim_id); }}
                                className="text-blue-600 hover:text-blue-800 underline text-xs"
                              >
                                {excerpts[claim.claim_id] === 'loading' ? 'Loading...' : 'View source context'}
                              </button>
                            </div>
                            {(() => {
                              const exc = excerpts[claim.claim_id];
                              if (!exc || exc === 'loading') return null;
                              if (exc === 'error') return <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">Transcript not available</div>;
                              if (!exc.messages) return <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">{exc.note || (exc.match_found === false ? 'Claim text not located in transcript — may have been paraphrased during extraction.' : 'No transcript content available.')}</div>;
                              return (
                                <div className="mt-1 space-y-1">
                                  {exc.match_found === false && (
                                    <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-center gap-1.5">
                                      <HiExclamationCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                      Claim text not located in this transcript. The messages below are surrounding context, not a verified source.
                                    </div>
                                  )}
                                <div className="border border-gray-200 rounded max-h-64 overflow-y-auto bg-gray-50">
                                  <div className="px-2 py-1 bg-gray-100 text-xs text-gray-500 sticky top-0 border-b">
                                    {exc.conversation_name || 'Untitled'} — {exc.turn_count} turns
                                    {exc.match_message_index !== undefined && <span className="ml-1">(matched at message {exc.match_message_index})</span>}
                                  </div>
                                  {exc.messages.map((m, mi) => (
                                    <div key={mi} className={`px-2 py-1 text-xs border-b border-gray-100 ${
                                      m.is_match ? 'bg-yellow-100 border-l-2 border-l-yellow-400' :
                                      m.sender === 'human' ? 'bg-blue-50/50' : 'bg-white'
                                    }`}>
                                      <span className="text-gray-300 font-mono mr-1">{m.index}</span>
                                      <span className={`font-semibold ${m.sender === 'human' ? 'text-blue-600' : 'text-green-600'}`}>{m.sender}:</span>{' '}
                                      <span className="text-gray-700 whitespace-pre-wrap">{m.text && m.text.length > 500 ? m.text.slice(0, 500) + '...' : m.text}</span>
                                    </div>
                                  ))}
                                </div>
                                </div>
                              );
                            })()}
                          </div>
                        ) : claim.source_object_type === 'archive_extraction' ? (
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-center gap-1.5">
                            <HiExclamationCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            Source conversation not linked
                          </div>
                        ) : null}
                        {/* Currency actions for conflicted claims */}
                        {claim.canonical_conflicts && claim.canonical_conflicts.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-gray-200 flex items-center gap-2">
                            <span className="text-xs text-gray-500 mr-1">Confirm as:</span>
                            <Button size="xs" color="warning" onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              doAction(claim, 'confirm', { currency: 'superseded' });
                            }} disabled={isProcessing}>
                              Superseded
                            </Button>
                            <Button size="xs" color="dark" onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              doAction(claim, 'confirm', { currency: 'historical_only' });
                            }} disabled={isProcessing}>
                              Historical
                            </Button>
                            <Button size="xs" color="success" onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              doAction(claim, 'confirm');
                            }} disabled={isProcessing}>
                              Current (no conflict)
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expand toggle */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setExpanded(prev => {
                        const next = new Set(prev);
                        if (next.has(claim.claim_id)) next.delete(claim.claim_id);
                        else next.add(claim.claim_id);
                        return next;
                      });
                      if (claim.canonical_conflicts && claim.canonical_conflicts.length > 0) {
                        setConflictReviewed(prev => new Set(prev).add(claim.claim_id));
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    tabIndex={-1}
                  >
                    {isExpanded ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
                  </button>

                  {/* Action buttons */}
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        const hasConflict = claim.canonical_conflicts && claim.canonical_conflicts.length > 0;
                        if (hasConflict && !conflictReviewed.has(claim.claim_id)) {
                          showToast('Canonical conflict — expand (x) to review, then confirm', 'error');
                        } else {
                          doAction(claim, 'confirm');
                        }
                      }}
                      disabled={isProcessing}
                      className={`p-1.5 rounded disabled:opacity-50 ${
                        claim.canonical_conflicts && claim.canonical_conflicts.length > 0 && !conflictReviewed.has(claim.claim_id)
                          ? 'hover:bg-orange-100 text-orange-400'
                          : 'hover:bg-green-100 text-green-600'
                      }`}
                      title={claim.canonical_conflicts && claim.canonical_conflicts.length > 0 && !conflictReviewed.has(claim.claim_id)
                        ? 'Canonical conflict — expand to review first'
                        : 'Confirm (a)'}
                      tabIndex={-1}
                    >
                      <HiCheck className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('reject'); }}
                      disabled={isProcessing}
                      className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                      title="Dispute (r)"
                      tabIndex={-1}
                    >
                      <HiX className="h-4 w-4" />
                    </button>
                    {isCurator && (
                      <button
                        onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('reject'); setRetractMode(true); }}
                        disabled={isProcessing}
                        className="p-1.5 rounded hover:bg-purple-100 text-purple-600 disabled:opacity-50"
                        title="Retract — curator only (t)"
                        tabIndex={-1}
                      >
                        <HiExclamation className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('defer'); }}
                      disabled={isProcessing}
                      className="p-1.5 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-50"
                      title="Defer (d)"
                      tabIndex={-1}
                    >
                      <HiClock className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Inline reject picker */}
                {showRejectPicker && (
                  <div className={`mt-2 ml-8 p-2 border rounded-lg ${retractMode ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`}>
                    <span className={`text-xs font-medium mb-1 block ${retractMode ? 'text-purple-700' : 'text-red-700'}`}>
                      {retractMode ? 'Retract' : 'Dispute'} reason (1-4, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {REASON_CODES.map(rc => (
                        <button
                          key={rc.value}
                          onClick={() => { doAction(claim, retractMode ? 'retract' : 'dispute', { reason_code: rc.value }); setRetractMode(false); }}
                          className={`px-2 py-1 text-xs rounded border ${retractMode ? 'border-purple-200 hover:bg-purple-100 text-purple-700' : 'border-red-200 hover:bg-red-100 text-red-700'}`}
                        >
                          <kbd className="font-mono mr-1">{rc.key}</kbd>{rc.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline defer picker */}
                {showDeferPicker && (
                  <div className="mt-2 ml-8 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-xs font-medium text-amber-700 mb-1 block">
                      Defer until (1-4, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {DEFER_PRESETS.map(dp => {
                        const date = new Date();
                        date.setDate(date.getDate() + dp.days);
                        return (
                          <button
                            key={dp.key}
                            onClick={() => doAction(claim, 'defer', {
                              notes: `deferred until ${date.toISOString().slice(0, 10)}`,
                            })}
                            className="px-2 py-1 text-xs rounded border border-amber-200 hover:bg-amber-100 text-amber-700"
                          >
                            <kbd className="font-mono mr-1">{dp.key}</kbd>{dp.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isProcessing && isActioning === claim.claim_id && (
                  <div className="mt-2 ml-8 flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Processing...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {(viewMode === 'conversation' || viewMode === 'subject') ? (
        totalGroups > 20 && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-gray-500">
              Page {page} of {Math.ceil(totalGroups / 20)} ({totalGroups} groups, {totalCount} claims)
            </span>
            <div className="flex gap-2">
              <Button size="xs" color="light" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button size="xs" color="light" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(totalGroups / 20)}>
                Next
              </Button>
            </div>
          </div>
        )
      ) : (
        totalCount > 50 && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-gray-500">
              Page {page} of {Math.ceil(totalCount / 50)} ({totalCount} claims)
            </span>
            <div className="flex gap-2">
              <Button size="xs" color="light" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button size="xs" color="light" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(totalCount / 50)}>
                Next
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ClaimsReview;
